import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Shield, Trash2, Users } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";

export default function PrivacySettings() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `novaxlove-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("app.account.exportCta"));
    } catch (e) {
      toast.error(extractError(e, t("app.account.exportError")));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="privacy-settings-page">
      <button
        onClick={() => navigate("/app/settings")}
        className="flex items-center gap-2 text-sm mb-4"
        style={{ color: "hsl(var(--nvl-ink-soft))" }}
        data-testid="privacy-settings-back"
      >
        <ArrowLeft size={14} /> {t("app.nav.settings")}
      </button>

      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {t("app.account.section")}
      </p>
      <h1 className="mt-3 text-4xl md:text-5xl">{t("app.account.privacyTitle")}</h1>
      <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {t("app.account.privacySubtitle")}
      </p>

      <div className="mt-8 space-y-6">
        <div className="nvl-card p-6 flex items-start gap-4">
          <Users size={20} style={{ color: "hsl(var(--nvl-accent))" }} />
          <div className="flex-1">
            <div className="font-medium">{t("app.moderation.blockedListTitle")}</div>
            <div className="text-sm mt-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.moderation.blockedListSubtitle")}
            </div>
          </div>
          <button
            onClick={() => navigate("/app/settings/blocked-users")}
            className="nvl-btn nvl-btn-outline text-sm"
            data-testid="privacy-open-blocked-users"
          >
            {t("app.moderation.overflowMenu")}
          </button>
        </div>

        <div className="nvl-card p-6 flex items-start gap-4">
          <Download size={20} style={{ color: "hsl(var(--nvl-accent))" }} />
          <div className="flex-1">
            <div className="font-medium">{t("app.account.exportTitle")}</div>
            <div className="text-sm mt-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.account.exportBody")}
            </div>
          </div>
          <button
            onClick={download}
            disabled={downloading}
            className="nvl-btn nvl-btn-outline text-sm disabled:opacity-50"
            data-testid="privacy-download-data"
          >
            {downloading ? t("app.moderation.loading") : t("app.account.exportCta")}
          </button>
        </div>

        <div className="nvl-card p-6 flex items-start gap-4">
          <Shield size={20} style={{ color: "hsl(var(--nvl-accent))" }} />
          <div className="flex-1">
            <div className="font-medium">{t("landing.footer.privacy")}</div>
            <div className="text-sm mt-1 flex flex-wrap gap-4">
              <a href="/legal/privacy" target="_blank" rel="noreferrer"
                 className="underline" data-testid="privacy-open-privacy-policy">
                {t("app.legal.privacyTitle")}
              </a>
              <a href="/legal/terms" target="_blank" rel="noreferrer"
                 className="underline" data-testid="privacy-open-terms">
                {t("app.legal.termsTitle")}
              </a>
              <a href="/legal/cookies" target="_blank" rel="noreferrer"
                 className="underline" data-testid="privacy-open-cookies">
                {t("app.legal.cookiesTitle")}
              </a>
            </div>
          </div>
        </div>

        <div className="nvl-card p-6 flex items-start gap-4 border"
             style={{ borderColor: "hsl(var(--destructive) / 0.35)" }}>
          <Trash2 size={20} style={{ color: "hsl(var(--destructive))" }} />
          <div className="flex-1">
            <div className="font-medium">{t("app.account.deleteTitle")}</div>
            <div className="text-sm mt-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.account.deleteSubtitle")}
            </div>
          </div>
          <button
            onClick={() => navigate("/app/settings/delete-account")}
            className="nvl-btn nvl-btn-outline text-sm"
            style={{ borderColor: "hsl(var(--destructive))",
                     color: "hsl(var(--destructive))" }}
            data-testid="privacy-open-delete-account"
          >
            {t("app.account.deleteCta")}
          </button>
        </div>
      </div>
    </div>
  );
}
